import { useState } from "react";
import { Loader2, Sparkles, X } from "lucide-react";

/**
 * SponsorshipCard — one row per active/expired sponsorship.
 *
 * Why a separate file: the inline tier-picker (extend/renew) makes this
 * the second-busiest component in the owner page after the EditPanel, and
 * it has its own local state (`open`) for the expanded picker.
 */
export default function SponsorshipCard({
  s,
  tiers,
  onExtend,
  extending,
  onCancel,
  cancelling,
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      data-testid={`owner-sponsorship-${s.place_id}`}
      className={`rounded-2xl border p-4 bg-white ${
        s.is_active ? "border-[#E1A624]" : "border-[#E5E1D8]"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-lg font-medium text-[#2A3B2C] line-clamp-1">
              {s.restaurant_name || "Restaurant"}
            </h3>
            {s.cancelled_at ? (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#F2EDE3] text-[#8A9A8B]">
                Cancelled
              </span>
            ) : s.is_active ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-[#E1A624] to-[#D95D39] text-white">
                <Sparkles className="w-2.5 h-2.5" />
                Active
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#F2EDE3] text-[#8A9A8B]">
                Expired
              </span>
            )}
          </div>
          <p className="text-xs text-[#647466] mt-1">
            {s.tier_name} ·{" "}
            {s.cancelled_at
              ? `cancelled ${new Date(s.cancelled_at).toLocaleDateString()}`
              : s.is_active
              ? `${s.days_remaining} days left`
              : `ended ${new Date(s.featured_until).toLocaleDateString()}`}
          </p>
        </div>
        <div className="flex flex-col gap-1.5 items-end shrink-0">
          <button
            data-testid={`owner-extend-toggle-${s.place_id}`}
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] text-white text-xs font-medium"
          >
            {s.is_active ? "Extend" : "Renew"}
          </button>
          {s.is_active && !s.cancelled_at && onCancel && (
            <button
              data-testid={`owner-cancel-${s.place_id}`}
              onClick={onCancel}
              disabled={cancelling}
              className="inline-flex items-center gap-1 text-[10px] text-[#8A9A8B] hover:text-[#D95D39] transition-colors disabled:opacity-50"
              title="End featured status now"
            >
              {cancelling ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <>
                  <X className="w-3 h-3" />
                  Cancel
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {tiers.map((t) => {
            const key = `${s.claim_id}:${t.id}`;
            return (
              <button
                key={t.id}
                data-testid={`owner-extend-${s.place_id}-${t.id}`}
                onClick={() => onExtend(t.id)}
                disabled={extending === key}
                className={`relative rounded-xl border p-3 text-left transition-all hover:-translate-y-0.5 hover:shadow ${
                  t.popular
                    ? "border-[#D95D39] bg-[#FFF3EE]"
                    : "border-[#E5E1D8] bg-white"
                }`}
              >
                {t.discount_percent > 0 && (
                  <span className="absolute -top-2 -right-2 inline-flex items-center gap-0.5 h-5 px-1.5 rounded-full text-[9px] font-bold bg-[#2A3B2C] text-white">
                    SAVE {t.discount_percent}%
                  </span>
                )}
                <div className="text-xs font-display font-medium text-[#2A3B2C]">
                  {t.name}
                </div>
                <div className="flex items-baseline gap-1.5 mt-0.5">
                  <span className="font-display text-xl text-[#2A3B2C]">
                    ${Math.round(t.amount).toLocaleString()}
                  </span>
                  {t.original_amount && (
                    <span className="text-[10px] text-[#8A9A8B] line-through">
                      ${Math.round(t.original_amount).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#647466]">
                  +{t.duration_days} days
                </div>
                {extending === key && (
                  <Loader2 className="w-3 h-3 mt-1 animate-spin text-[#D95D39]" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
